// Artifact list, immutable version list, selection, metadata and preview.
//
// Refresh model: bounded polling only (see runtime/gini-handoff-polling.ts).
// The footer states the last poll time and offers an explicit manual
// refresh; it never says "live", "realtime", or "connected", because
// artifact push delivery does not exist.
//
// Download references are requested on demand and are bounded + expiring.
// The reference id is shown, never a storage path — the daemon's route only
// ever returns `{ referenceId, expiresAt }` for exactly that reason. An
// expired reference surfaces as a plain re-request affordance rather than a
// broken preview.

import { useEffect, useMemo, useState } from 'react';
import { Button } from '@open-design/components';
import type { GiniArtifactManifest, GiniHandoffCapability } from '@open-design/contracts/gini-platform';

import { useT } from '../../i18n';
import { requestArtifactDownloadReference } from '../../providers/gini-handoff';
import { useGiniHandoffArtifacts } from '../../runtime/gini-handoff-polling';
import { CapabilityNotice } from './CapabilityNotice';
import styles from './HandoffLanding.module.css';

interface Props {
  handoffId: string;
  pushDelivery: GiniHandoffCapability;
  onSelectionChange?: (selection: { artifactId: string; artifactVersion: number } | null) => void;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function ArtifactPanel({ handoffId, pushDelivery, onSelectionChange }: Props) {
  const t = useT();
  const { artifacts, phase, lastPolledAt, error, exhausted, refresh } = useGiniHandoffArtifacts(handoffId);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [reference, setReference] = useState<{ referenceId: string; expiresAt: string } | null>(null);
  const [referenceError, setReferenceError] = useState<string | null>(null);

  const versions = useMemo(() => {
    const flat: { key: string; manifest: GiniArtifactManifest }[] = [];
    for (const lineage of artifacts) {
      for (const manifest of lineage.versions) {
        flat.push({ key: `${lineage.artifactId}@${manifest.artifactVersion}`, manifest });
      }
    }
    return flat;
  }, [artifacts]);

  // Default to the newest known version, but never override a selection the
  // user made — a new version arriving mid-review must not yank the preview
  // out from under them.
  useEffect(() => {
    if (selectedKey !== null || versions.length === 0) return;
    setSelectedKey(versions[versions.length - 1]?.key ?? null);
  }, [versions, selectedKey]);

  const selected = versions.find((v) => v.key === selectedKey) ?? null;

  useEffect(() => {
    // A different version means the old reference no longer describes what
    // is on screen; drop it rather than letting it look current.
    setReference(null);
    setReferenceError(null);
    onSelectionChange?.(
      selected ? { artifactId: selected.manifest.artifactId, artifactVersion: selected.manifest.artifactVersion } : null,
    );
  }, [selectedKey]);

  async function requestReference(): Promise<void> {
    if (!selected) return;
    setReferenceError(null);
    const result = await requestArtifactDownloadReference(
      selected.manifest.artifactId,
      selected.manifest.artifactVersion,
      selected.manifest.entrypoint,
    );
    if (result.ok) {
      setReference({ referenceId: result.referenceId, expiresAt: result.expiresAt });
    } else {
      setReference(null);
      setReferenceError(result.message);
    }
  }

  const referenceExpired = reference !== null && new Date(reference.expiresAt).getTime() <= Date.now();

  return (
    <section className={styles.card} aria-labelledby="gini-handoff-artifacts">
      <h2 className={styles.cardTitle} id="gini-handoff-artifacts">
        {t('giniHandoff.artifactsHeading')}
        <Button type="button" variant="subtle" onClick={refresh} data-testid="gini-handoff-artifacts-refresh">
          {t('giniHandoff.refresh')}
        </Button>
      </h2>

      <CapabilityNotice capability={pushDelivery} testId="gini-handoff-push-notice" />

      {versions.length === 0 ? (
        <p className={styles.empty} data-testid="gini-handoff-artifacts-empty">
          {t('giniHandoff.artifactsEmpty')}
        </p>
      ) : (
        <ul className={styles.versionList} data-testid="gini-handoff-version-list">
          {versions.map((entry) => (
            <li key={entry.key}>
              <button
                type="button"
                className={`${styles.versionButton} ${entry.key === selectedKey ? styles.versionButtonSelected : ''}`}
                aria-pressed={entry.key === selectedKey}
                onClick={() => setSelectedKey(entry.key)}
              >
                {entry.manifest.title} · v{entry.manifest.artifactVersion}
              </button>
            </li>
          ))}
        </ul>
      )}

      {selected ? (
        <>
          <div className={styles.metaGrid} data-testid="gini-handoff-artifact-meta">
            <div>
              <span className={styles.metaLabel}>Entrypoint</span>
              <span className={styles.metaValue}>{selected.manifest.entrypoint}</span>
            </div>
            <div>
              <span className={styles.metaLabel}>Files</span>
              <span className={styles.metaValue}>{selected.manifest.files.length}</span>
            </div>
            <div>
              <span className={styles.metaLabel}>Total size</span>
              <span className={styles.metaValue}>{formatBytes(selected.manifest.sizeBytes)}</span>
            </div>
            <div>
              <span className={styles.metaLabel}>Created</span>
              <span className={styles.metaValue}>{selected.manifest.createdAt}</span>
            </div>
            <div>
              <span className={styles.metaLabel}>Source run</span>
              <span className={styles.metaValue}>{selected.manifest.sourceRunId}</span>
            </div>
            <div>
              <span className={styles.metaLabel}>Provenance</span>
              <span className={styles.metaValue}>
                {selected.manifest.provenance.agentId} · {selected.manifest.provenance.modelSelectionOwner}
              </span>
            </div>
            <div>
              <span className={styles.metaLabel}>Checksum (sha256)</span>
              <span className={styles.metaValue}>{selected.manifest.sha256}</span>
            </div>
          </div>

          <div className={styles.actions}>
            <Button type="button" variant="default" onClick={() => void requestReference()} data-testid="gini-handoff-preview">
              {t('giniHandoff.preview')}
            </Button>
          </div>

          {referenceError ? (
            <p className={styles.empty} data-testid="gini-handoff-reference-error">
              {referenceError}
            </p>
          ) : null}

          {reference ? (
            referenceExpired ? (
              <p className={styles.empty} data-testid="gini-handoff-reference-expired">
                {t('giniHandoff.referenceExpired')}
              </p>
            ) : (
              <iframe
                className={styles.previewFrame}
                title={selected.manifest.title}
                // Bounded, expiring reference id — not a storage path.
                src={`/api/gini-platform/artifacts/references/${encodeURIComponent(reference.referenceId)}`}
                sandbox="allow-scripts"
                data-testid="gini-handoff-preview-frame"
              />
            )
          ) : null}
        </>
      ) : null}

      <div className={styles.pollRow}>
        <span data-testid="gini-handoff-poll-status">
          {exhausted
            ? t('giniHandoff.pollingStopped')
            : phase === 'polling'
              ? t('giniHandoff.checking')
              : lastPolledAt
                ? `${t('giniHandoff.lastChecked')} ${lastPolledAt}`
                : t('giniHandoff.checking')}
        </span>
        {error && !exhausted ? <span>{error}</span> : null}
      </div>
    </section>
  );
}
